import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./MySubscriptionsView.styles";
import SubscriptionModel from "../../models/subscription/SubscriptionModel";
import * as SubscriptionAction from "../../stores/subscriptions/SubscriptionAction";
import UniversalTable from "../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../components/UniversalTable/interfaces";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../selectors/requesting/RequestingSelector";
import { useAuthContext } from "../../auth/AuthProvider";
import { useHistory } from "react-router-dom";
import { Breadcrumbs } from "../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import { toDateRange } from "../../utilities/dateTimeUtils";
import { TableAccordionElement } from "../components/UniversalTable/TableAccordionElement";
import { BaseCurrenciesComponent } from "./admin/BaseCurrenciesComponent";
import InfoIcon from "@material-ui/icons/Info";
import CrossedEyeIcon from "@material-ui/icons/VisibilityOff";
import { useGetSubscriptionTypeForProduct } from "../../hooks/useGetSubscriptionTypeForProduct";
import { SINGLE_ISIN_PRODUCT_ID } from "../../constants/REPORTS";

export interface IMySubscriptionsViewProps extends WithStyles<typeof styles> {
}

const MySubscriptionsViewPure: React.FunctionComponent<IMySubscriptionsViewProps> = ({
                                                                                         classes
                                                                                     }: IMySubscriptionsViewProps) => {
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const singleIsinSubscriptionId = useGetSubscriptionTypeForProduct(SINGLE_ISIN_PRODUCT_ID);

    const [detailsDialogOpen, setDetailsDialogOpen] = React.useState<boolean>(false);
    const [displayedSubscriptionIndex, setDisplayedSubscriptionIndex] = React.useState<number>(null);
    const [displayedRangeIndex, setDisplayedRangeIndex] = React.useState<number>(null);

    useEffect(() => {
        dispatch(SubscriptionAction.requestUserSubscriptions(userId));
    }, [userId, dispatch]);

    const userSubscriptionsFromState = useSelector(state => state.subscriptions?.userSubscriptions);
    const isRequesting = useSelector(state => selectRequesting(state, [SubscriptionAction.REQUEST_USER_SUBSCRIPTIONS]));

    let userSubscription: IUniversalTableData;
    if (userSubscriptionsFromState) {
        userSubscription = Object.values(userSubscriptionsFromState)?.map((subscription: SubscriptionModel) => ({
            id: subscription.id,
            typeId: subscription.typeId,
            name: subscription.name,
            description: subscription.description,
            dateRanges: subscription.dateRanges,
            isFilterable: subscription.isFilterable
        }));
    }

    const handleShowFilesClick = index => {
        let path = "/my-account/my-subscriptions/" + userSubscription[index]?.id;
        history.push(path);
    };

    function hasMultipleElements(items) {
        return items?.ranges?.length > 1;
    }

    const handleShowDetails = (rowId, index) => {
        setDisplayedSubscriptionIndex(rowId);
        setDisplayedRangeIndex(index);
        setDetailsDialogOpen(true);
    };

    const disableReasons = [
        {
            id: "KAIKO",
            condition: (index) => userSubscription[index].isFilterable,
            tooltip: "Use Customer API to download the files"
        },
        {
            id: "SINGLE_ISIN_REPORT",
            condition: (index) => singleIsinSubscriptionId === userSubscription[index].typeId,
            tooltip: "Go to My requests/reports section to download the files"
        }
    ];

    const getDisableReason = (index) => disableReasons?.find(r => r.condition(index));

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.name"),
            field: "name"
        },
        {
            type: "text",
            title: t("general.description"),
            field: "description"
        },
        {
            type: "component",
            title: "Time range",
            field: "dateRanges",
            width: 500,
            component: (items, rowId) => hasMultipleElements(items)
                ? <TableAccordionElement rowId={rowId} labelCollapsed="Multiple ranges" detailElementUncollapsed={
                        <div>
                            {items?.ranges.map((range, index) => <div key={index}>
                                <div className={classes.rowDisplay}>
                                    <Typography variant={"body2"}>
                                        {toDateRange(range)}
                                    </Typography>
                                    {range.levelTwoItems.length > 0
                                    ? <Tooltip title="Show assets">
                                        <IconButton className={classes.infoButton} onClick={() => handleShowDetails(rowId, index)}>
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    : null}
                                    {!range.isVisible
                                    ? <Tooltip title="Date range visibility is expired">
                                        <CrossedEyeIcon fontSize="small" />
                                    </Tooltip>
                                    : null}
                                </div>                                    
                            </div>
                            )}
                        </div>
                    }
                />
                : <div className={classes.rowDisplay}>
                    <Typography>{toDateRange(items?.ranges[0])}</Typography>
                    {userSubscription[rowId].isFilterable
                    ? <Tooltip title="Show assets">
                        <IconButton className={classes.infoButton} onClick={() => handleShowDetails(rowId, [0])}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    : null}
                    {!items?.ranges[0].isVisible
                    ? <Tooltip title="Date range visibility is expired">
                        <CrossedEyeIcon fontSize="small" />
                    </Tooltip>
                    : null}
                </div>
        },
        {
            title: t("general.files"),
            type: "button",
            buttonTitle: t("manageAccount.mySubscriptions.showFiles"),
            onClick: index => handleShowFilesClick(index),
            disabled: index => getDisableReason(index) !== undefined,
            disabledTooltip: (index) => getDisableReason(index)?.tooltip || ""
        }
    ];

    return (<>
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    {title: t("manageAccount.myAccount"), href: "/my-account"},
                    {title: t("manageAccount.mySubscriptions.mySubscriptions"), href: "/my-account/my-subscriptions"}
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">{t("manageAccount.mySubscriptions.mySubscriptions")}</Typography>
            </div>
            <UniversalTable columns={columns} data={userSubscription} isLoading={isRequesting} searchable />
        </CenteredContainer>
        
        {userSubscription && <BaseCurrenciesComponent
            currencies={userSubscription[displayedSubscriptionIndex]?.dateRanges?.ranges[displayedRangeIndex]?.levelTwoItems.map(currency => currency.name).join(", ")}
            detailsDialogOpen={detailsDialogOpen}
            setDetailsDialogOpen={setDetailsDialogOpen}
        />}
    </>);
};

export const MySubscriptionsView = withStyles(styles)(MySubscriptionsViewPure);
